import React from "react";
import ReactDOM from "react-dom/client";
import { ApolloProvider, type ApolloClient } from "@apollo/client";
import {
  decodeToken,
  isTokenValid,
  LogLevel,
  serviceLineMapper,
  WidgetConfig,
  WidgetOptions,
} from "./utils/common";
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material";
import App from "./App";
import theme from "./theme/Theme";
import { getPermissionPolicies } from "./queries/PermissionPolicies";
import { policiesContext } from "./utils/common";
import { SnackbarProvider } from "./context/AlertsContext";
import { getCoderAssignmentConfig } from "./queries/configuration";
import {
  createServiceDeskApi,
  setServiceDeskApi,
  type ServiceDeskApi,
} from "./api";
import { shouldAttachBearerAuthorization } from "@codametrix/client-api";

class ServiceDesk {
  theme: any;
  /** Latest bearer for UI decode only — network auth is always live via api.getBearerToken */
  bearerToken: string = "";
  config: WidgetConfig;
  logLevel: LogLevel;
  api: ServiceDeskApi;
  apolloClient: ApolloClient<any>;
  et: EventTarget;

  constructor(config: WidgetConfig) {
    this.config = config;
    this.logLevel = config?.logLevel || LogLevel.ERROR;
    if ("theme" in config && config.theme) {
      let newTheme = config.theme;
      if ("palette" in config.theme) {
        Object.keys(config.theme.palette).forEach((prop) => {
          newTheme.palette[prop] = {
            main: config.theme.palette[prop],
          };
        });
      }
      this.theme = createTheme(theme, newTheme);
    } else {
      this.theme = theme;
    }
    this.et = new EventTarget();
    this.registerTeardown();

    this.api =
      config.api ??
      createServiceDeskApi({
        getBearerToken: () => config.getBearerToken(),
        bffURL: config.bffURL,
        cacheProfile: "worklist",
        onApolloClientReady: config.onApolloClientReady,
      });
    setServiceDeskApi(this.api);
    this.apolloClient = this.api.apollo;
    if (config.api && config.onApolloClientReady) {
      config.onApolloClientReady(this.apolloClient);
    }

    this.api
      .getBearerToken()
      .then((bearerToken) => {
        this.bearerToken = bearerToken;
        const event = document.createEvent("Event");
        event.initEvent("ready", true, true);
        this.et.dispatchEvent(event);
      })
      .catch((err) => {
        console.error("Failed to receive bearer token.", err);
      });
  }

  static init(config: WidgetConfig): ServiceDesk {
    return new ServiceDesk(config);
  }

  static createServiceDeskApi = createServiceDeskApi;
  static setServiceDeskApi = setServiceDeskApi;

  async refreshToken() {
    const token = await this.api.getBearerToken();
    this.bearerToken = token;
    return token;
  }

  async renderWidget(
    widgetName: string,
    elementId: string,
    apolloClient: any,
    widgetOptions?: WidgetOptions
  ) {
    let policies = [];
    let coderAssignmentConfig = [];
    try {
      const [policiesResponse, coderAssignmentConfigResponse] = await Promise.all([
        this.apolloClient?.query({
          query: getPermissionPolicies,
          fetchPolicy: "no-cache",
        }),
        this.apolloClient?.query({
          query: getCoderAssignmentConfig,
          variables: {
            service:
              serviceLineMapper[widgetOptions?.userContext?.serviceLine?.name] ??
              widgetOptions?.userContext?.serviceLine?.name,
          },
          fetchPolicy: "cache-first",
        }),
      ]);

      policies = policiesResponse?.data?.PermissionPolicies?.data;
      coderAssignmentConfig = coderAssignmentConfigResponse?.data?.CoderAssignmentConfig;
    } catch (err) {
      console.error("Failed to fetch policies and coder assignment config:", err);
    }

    if (!this.bearerToken) {
      this.bearerToken = await this.api.getBearerToken();
    }
    const decodedPayload: any = decodeToken(this.bearerToken);

    const mount = () => {
      const root = ReactDOM.createRoot(
        document.getElementById(elementId) as HTMLElement
      );
      sessionStorage.setItem(
        "tokenPayload",
        JSON.stringify(decodeToken(this.bearerToken))
      );
      root.render(
        <React.StrictMode>
          <ApolloProvider client={this.apolloClient}>
            <ThemeProvider theme={this.theme}>
              <CssBaseline />
              <policiesContext.Provider
                value={{
                  policies,
                  roles: decodedPayload?.roles,
                  userContext: widgetOptions?.userContext,
                  coderAssignmentConfig,
                }}
              >
                <SnackbarProvider>
                  <App
                    token={decodeToken(this.bearerToken)}
                    widgetName={widgetName}
                    client={apolloClient}
                    widgetOptions={widgetOptions}
                  />
                </SnackbarProvider>
              </policiesContext.Provider>
            </ThemeProvider>
          </ApolloProvider>
        </React.StrictMode>
      );
    };

    if (!this.apolloClient) {
      this.et.addEventListener("ready", () => {
        mount();
      });
    } else {
      mount();
    }
  }

  /**
   * Remove all MUI overlay portals (Popovers, Menus, Dialogs, Drawers,
   * Tooltips) that the service desk rendered onto document.body, and
   * restore aria-hidden state that MUI sets on sibling elements while
   * an overlay is open.
   */
  static teardown() {
    document.body
      .querySelectorAll(':scope > .MuiModal-root, :scope > .MuiPopper-root')
      .forEach((node) => node.remove());

    document.getElementById('root')?.removeAttribute('aria-hidden');
  }

  private registerTeardown() {
    if (!ServiceDesk.teardownRegistered) {
      globalThis.addEventListener('cmx-logout', ServiceDesk.teardown);
      ServiceDesk.teardownRegistered = true;
    }
  }

  private static teardownRegistered = false;

  create(widgetName: string, elementId: string, widgetOptions?: WidgetOptions) {
    const start = async () => {
      // Session-first: cookie authenticates the BFF; do not block mount on JWT exp.
      if (shouldAttachBearerAuthorization()) {
        if (!this.bearerToken || !isTokenValid(this.bearerToken)) {
          const token = await this.refreshToken();
          if (!isTokenValid(token)) {
            throw new Error("Received invalid token on refresh");
          }
        }
      } else if (!this.bearerToken) {
        try {
          this.bearerToken = await this.api.getBearerToken();
        } catch {
          // Optional for UI decode only when Authorization is omitted.
        }
      }
      await this.renderWidget(
        widgetName,
        elementId,
        this.apolloClient,
        widgetOptions
      );
    };

    start().catch((err) => {
      console.error(`Failed to render Xcaliber widget - ${err}`);
    });
  }
}

export const init = ServiceDesk.init;
export {
  createServiceDeskApi,
  getServiceDeskApi,
  setServiceDeskApi,
} from "./api";
export type {
  CreateServiceDeskApiOptions,
  GetBearerToken,
  ServiceDeskApi,
  ServiceDeskCacheProfile,
} from "./api";

export default ServiceDesk;
